import React from "react";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import BackHandIcon from "@mui/icons-material/BackHand";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";

function CheckpointRecordTable({ record, onBack, employeeAvatarColorByUserId }) {
  const formatDateReadable = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasSubmissionData = record.submissions && record.submissions.length > 0;
  const hasIncompleteData =
    record.incomplete_assignments && record.incomplete_assignments.length > 0;
  const hasEmployeeData = hasSubmissionData || hasIncompleteData;

  const exportCheckpointCSV = () => {
    const rows = [];
    (record.submissions || []).forEach((submission) => {
      rows.push({
        "Employee": submission.employee_name,
        "Status": "Approved",
        "Approved By": submission.approved_by_name || "",
        "Date Approved": submission.date_completed
          ? formatDateReadable(submission.date_completed)
          : "",
      });
    });
    (record.incomplete_assignments || []).forEach((assignment) => {
      rows.push({
        "Employee": assignment.employee_name,
        "Status": "Pending Manager Approval",
        "Approved By": "",
        "Date Approved": "",
      });
    });

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    downloadCSV(csv, `checkpoint_${record.name.replace(/\s+/g, "_")}.csv`);
  };

  return (
    <div className="px-6 py-8">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium mb-4 transition-colors"
        >
          <ChevronLeftIcon className="-ml-2" style={{ fontSize: "24px" }} />
          Back to Records
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {record.name}
            </h2>
            <p className="text-sm text-slate-600">Checkpoint approval records</p>
          </div>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            onClick={exportCheckpointCSV}
          >
            <DownloadIcon style={{ fontSize: "20px" }} />
            <span className="font-medium">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Checkpoint Details Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Category
            </div>
            <div className="text-sm text-slate-900">
              {record.category || "Manager Approval"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Type
            </div>
            <div className="text-sm font-medium text-slate-900">
              Manager Sign-Off Required
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Total Approvals
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.total_submissions} / {record.assigned_count}
            </div>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="-mx-6 border-y border-slate-200 bg-white">
        {hasEmployeeData ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Approved By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date Approved
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {record.submissions.map((submission, index) => (
                  <tr
                    key={`completed-${index}`}
                    className="hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={submission.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[
                                submission.employee_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-900">
                            {submission.employee_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon
                          className="text-emerald-600"
                          style={{ fontSize: "20px" }}
                        />
                        <span className="text-sm font-medium text-emerald-700">
                          Approved
                        </span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      {submission.approved_by_name ? (
                        <div className="flex items-center gap-2">
                          <VerifiedUserIcon
                            className="text-slate-400"
                            style={{ fontSize: "18px" }}
                          />
                          <span className="text-sm text-slate-900">
                            {submission.approved_by_name}
                          </span>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400">—</div>
                      )}
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CalendarTodayIcon style={{ fontSize: "16px" }} />
                        <span>{formatDateReadable(submission.date_completed)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {record.incomplete_assignments?.map((assignment, index) => (
                  <tr
                    key={`pending-${index}`}
                    className="hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={assignment.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[
                                assignment.employee_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-900">
                            {assignment.employee_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <BackHandIcon
                          className="text-amber-600"
                          style={{ fontSize: "20px" }}
                        />
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                          Pending Manager Approval
                        </span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">—</div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">—</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <InfoOutlinedIcon
              className="text-slate-300 mx-auto mb-4"
              style={{ fontSize: "48px" }}
            />
            <p className="text-slate-600 font-medium mb-2">
              Employee-level checkpoint data not available
            </p>
            <p className="text-slate-400 text-sm">
              Summary: {record.total_submissions} of {record.assigned_count} employees
              have been approved at this checkpoint.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CheckpointRecordTable;
